import { createSlice } from '@reduxjs/toolkit';

// UI-only preferences. Server data (termsAccepted) has moved to React Query
// via `useFetchUser`. `setUserData` is still dispatched once on user load to
// hydrate the UI prefs below from user metadata.
const userSlice = createSlice({
  name: 'user',
  initialState: {
    select: 'Acuity',
    viewMode: 'Detailed',
    colorScheme: 'default',
    columnCount: 4,
    chairListView: {
      columns: null,
      filters: {
        basicFilters: {},
        advancedFilters: [],
        relationship: 'AND'
      },
      sortConfig: { key: '', direction: 'asc' }
    },
    nurseListView: {
      columns: null,
      filters: {
        basicFilters: {},
        advancedFilters: [],
        relationship: 'AND'
      },
      sortConfig: { key: '', direction: 'asc' }
    },
  },
  reducers: {
    setUserData: (state, action) => {
      state.select = action.payload.select || 'Acuity';
      state.viewMode = action.payload.viewMode || 'Detailed';
      state.colorScheme = action.payload.colorScheme || 'default';
      state.columnCount = action.payload.columnCount || 4;

      // Load list view preferences if they exist in user metadata
      if (action.payload.chairListView) {
        state.chairListView = {
          ...state.chairListView,
          ...action.payload.chairListView
        };
      }

      if (action.payload.nurseListView) {
        state.nurseListView = {
          ...state.nurseListView,
          ...action.payload.nurseListView
        };
      }
    },
    setSelect: (state, action) => {
      state.select = action.payload;
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    setColorScheme: (state, action) => {
      state.colorScheme = action.payload;
    },
    setColumnCount: (state, action) => {
      state.columnCount = action.payload;
    },
    // New reducers for chair list view
    setChairListViewColumns: (state, action) => {
      state.chairListView.columns = action.payload;
    },
    setChairListViewFilters: (state, action) => {
      state.chairListView.filters = action.payload;
    },
    setChairListViewSortConfig: (state, action) => {
      state.chairListView.sortConfig = action.payload;
    },
    // New reducers for nurse list view
    setNurseListViewColumns: (state, action) => {
      state.nurseListView.columns = action.payload;
    },
    setNurseListViewFilters: (state, action) => {
      state.nurseListView.filters = action.payload;
    },
    setNurseListViewSortConfig: (state, action) => {
      state.nurseListView.sortConfig = action.payload;
    },
    // Generic action creators for context-specific list views
    setListViewColumns: (state, action) => {
      const { path, data } = action.payload;
      if (!state[path]) {
        state[path] = {
          columns: null,
          filters: {
            basicFilters: {},
            advancedFilters: [],
            relationship: 'AND'
          },
          sortConfig: { key: '', direction: 'asc' }
        };
      }
      state[path].columns = data;
    },
    setListViewFilters: (state, action) => {
      const { path, data } = action.payload;
      if (!state[path]) {
        state[path] = {
          columns: null,
          filters: {
            basicFilters: {},
            advancedFilters: [],
            relationship: 'AND'
          },
          sortConfig: { key: '', direction: 'asc' }
        };
      }
      state[path].filters = data;
    },
    setListViewSortConfig: (state, action) => {
      const { path, data } = action.payload;
      if (!state[path]) {
        state[path] = {
          columns: null,
          filters: {
            basicFilters: {},
            advancedFilters: [],
            relationship: 'AND'
          },
          sortConfig: { key: '', direction: 'asc' }
        };
      }
      state[path].sortConfig = data;
    },
  },
});

export const {
  setUserData,
  setSelect,
  setViewMode,
  setColumnCount,
  setColorScheme,
  setChairListViewColumns,
  setChairListViewFilters,
  setChairListViewSortConfig,
  setNurseListViewColumns,
  setNurseListViewFilters,
  setNurseListViewSortConfig,
  setListViewColumns,
  setListViewFilters,
  setListViewSortConfig
} = userSlice.actions;

export default userSlice.reducer;
